/**
 * The "What's new" slides' previews (artifact v8, 2026-10-09). All follow
 * both themes through design tokens, and none carries a real person's data.
 *
 *   - Bullseye: an inline SVG drawing, unchanged since #1032.
 *   - Combine & Filter: an inline SVG of the flow, People Lists of any kind
 *     in, one combined People List out, then AI Review or Company Map.
 *   - Candidates (2026-10-27): an inline SVG of the candidate panel, the
 *     fictional Gregory Tan, with the four reports and Download in one box.
 *     It replaced a dark screenshot, which stayed dark in the light theme and
 *     went stale with every change to the picker.
 *   - AI Review: a miniature of the prompt viewer, with fictional text.
 *   - ChopBook (artifact v16, slide 5): an inline SVG of PitchBook rows
 *     ticked, captured by ChopBook into a Company List on a search, and one
 *     company record. Fictional companies.
 *   - Thrive import (2026-10-20): the three ways into a People List, ChopLink,
 *     an uploaded file and, new and starred, a Thrive search; the raw list it
 *     makes, tagged Thrive with its stage columns; and a Thrive search it can
 *     be added to, as Identified. Fictional, no names.
 *   - Criteria suggested from Thrive (2026-11-02): the AI Review criteria,
 *     two written by the recruiter and, under them in the violet `info` AI
 *     tone with a sparkle, two suggested from the search's decisions, each
 *     with its evidence and an Add. Fictional criteria.
 *
 * The drawings and the miniature are decorative (`aria-hidden`): the
 * slide's text carries the meaning.
 *
 * Every preview sits on the `surface-raised` panel, so the Bullseye's
 * separators are cut in that colour, as the real Bullseye cuts its own in
 * `surface`.
 */
import type { ComponentType, ReactNode } from "react";

import type { WhatsNewIllustration } from "./whatsNew";

/** The Bullseye canvas: landscape, decorative, out of the tab order. */
function Canvas({ children }: { children: ReactNode }) {
  return (
    <svg viewBox="0 0 240 140" aria-hidden="true" focusable="false" className="h-48 w-full">
      {children}
    </svg>
  );
}

/** Polar point around the target's centre; 0° is up, clockwise. */
function polar(r: number, deg: number): [number, number] {
  const a = ((deg - 90) * Math.PI) / 180;
  return [120 + r * Math.cos(a), 70 + r * Math.sin(a)];
}

const RINGS = [
  { r: 64, fill: "fill-tenure-1" },
  { r: 51, fill: "fill-tenure-2" },
  { r: 38, fill: "fill-tenure-3" },
  { r: 25, fill: "fill-tenure-4" },
  { r: 12, fill: "fill-tenure-5" },
] as const;

/** Three lists, three slices, a few people each, kept clear of every cut. */
const DOTS = [
  { r: 18, deg: 40, fill: "fill-chart-7" },
  { r: 44, deg: 75, fill: "fill-chart-7" },
  { r: 57, deg: 20, fill: "fill-chart-7" },
  { r: 31, deg: 160, fill: "fill-chart-8" },
  { r: 44, deg: 200, fill: "fill-chart-8" },
  { r: 57, deg: 140, fill: "fill-chart-8" },
  { r: 18, deg: 290, fill: "fill-chart-2" },
  { r: 31, deg: 330, fill: "fill-chart-2" },
  { r: 57, deg: 265, fill: "fill-chart-2" },
] as const;

function BullseyeDrawing() {
  return (
    <Canvas>
      {RINGS.map((ring) => (
        <circle key={ring.r} cx={120} cy={70} r={ring.r} className={ring.fill} />
      ))}
      {RINGS.map((ring) => (
        <circle
          key={`cut-${ring.r}`}
          cx={120}
          cy={70}
          r={ring.r}
          fill="none"
          strokeWidth={2}
          className="stroke-surface-raised"
        />
      ))}
      {[0, 120, 240].map((deg) => {
        const [x, y] = polar(64, deg);
        return (
          <line
            key={deg}
            x1={120}
            y1={70}
            x2={x}
            y2={y}
            strokeWidth={2}
            className="stroke-surface-raised"
          />
        );
      })}
      {DOTS.map((dot) => {
        const [x, y] = polar(dot.r, dot.deg);
        return (
          <circle
            key={`${dot.r}-${dot.deg}`}
            cx={x}
            cy={y}
            r={4}
            strokeWidth={1.5}
            className={`${dot.fill} stroke-surface`}
          />
        );
      })}
    </Canvas>
  );
}

/** Geometry from artifact v8: a 340 × 250 canvas. */
const SOURCES = [
  { x: 6, width: 74, title: "ChopLink", sub: "chop", dashed: false },
  { x: 88, width: 74, title: "CSV", sub: "upload", dashed: false },
  { x: 170, width: 74, title: "AI Review", sub: "list", dashed: false },
  { x: 252, width: 82, title: "…", sub: "N lists", dashed: true },
] as const;

/** The two next steps. Side by side, never overlapping: 60–160 and 180–280. */
const NEXT_STEPS = [
  { x: 60, label: "AI Review" },
  { x: 180, label: "Company Map" },
] as const;

const NEXT_WIDTH = 100;

function Label({
  x,
  y,
  children,
  tone = "text",
}: {
  x: number;
  y: number;
  children: ReactNode;
  tone?: "text" | "sub" | "bold";
}) {
  const look = {
    text: { fontSize: 11, className: "fill-text" },
    sub: { fontSize: 9.5, className: "fill-text-secondary" },
    bold: { fontSize: 12, className: "fill-text font-bold" },
  }[tone];
  return (
    <text x={x} y={y} textAnchor="middle" fontSize={look.fontSize} className={look.className}>
      {children}
    </text>
  );
}

function CombineFilterDrawing() {
  const line = "fill-none stroke-accent";
  return (
    <svg
      viewBox="0 0 340 250"
      aria-hidden="true"
      focusable="false"
      className="h-auto w-full max-w-sm"
    >
      {SOURCES.map((s) => (
        <g key={s.title}>
          <rect
            x={s.x}
            y={6}
            width={s.width}
            height={42}
            rx={6}
            strokeDasharray={s.dashed ? "4 3" : undefined}
            className="fill-surface stroke-border"
          />
          <Label x={s.x + s.width / 2} y={24}>
            {s.title}
          </Label>
          <Label x={s.x + s.width / 2} y={38} tone="sub">
            {s.sub}
          </Label>
        </g>
      ))}
      <path
        d="M43 48 V62 H293 V48 M125 48 V62 M207 48 V62 M170 62 V78"
        strokeWidth={1.6}
        className={line}
      />
      <path d="M164 74 L170 82 L176 74" strokeWidth={1.6} className={line} />
      <rect
        x={70}
        y={84}
        width={200}
        height={50}
        rx={7}
        className="fill-accent-muted stroke-accent"
      />
      <Label x={170} y={103} tone="bold">
        Combine &amp; Filter
      </Label>
      <Label x={170} y={120} tone="sub">
        ✓ not in Thrive · title · company · email
      </Label>
      <path d="M170 134 V150" strokeWidth={1.6} className={line} />
      <path d="M164 146 L170 154 L176 146" strokeWidth={1.6} className={line} />
      <rect
        x={70}
        y={156}
        width={200}
        height={34}
        rx={7}
        className="fill-success-muted stroke-success"
      />
      <Label x={170} y={177} tone="bold">
        Combined People List · 214
      </Label>
      {NEXT_STEPS.map((step) => {
        const mid = step.x + NEXT_WIDTH / 2;
        return (
          <g key={step.label} data-testid="whats-new-combine-next">
            <path d={`M${mid} 190 V210`} strokeWidth={1.6} className={line} />
            <path
              d={`M${mid - 6} 206 L${mid} 214 L${mid + 6} 206`}
              strokeWidth={1.6}
              className={line}
            />
            <rect
              x={step.x}
              y={216}
              width={NEXT_WIDTH}
              height={26}
              rx={13}
              strokeDasharray="4 3"
              className="fill-surface stroke-accent"
            />
            <Label x={mid} y={233}>
              {step.label}
            </Label>
          </g>
        );
      })}
    </svg>
  );
}

/** The three ways into a People List; the Thrive search is the new one. */
const IMPORT_SOURCES = [
  { x: 6, title: "ChopLink", sub: "chop", isNew: false },
  { x: 120, title: "File", sub: "CSV · manual", isNew: false },
  { x: 234, title: "Thrive search", sub: "pick stages", isNew: true },
] as const;

const IMPORT_SOURCE_WIDTH = 100;

/** A small five-point star centred on (cx, cy): marks what is new. */
function NewStar({ cx, cy, r = 6 }: { cx: number; cy: number; r?: number }) {
  const points = Array.from({ length: 10 }, (_, i) => {
    const radius = i % 2 === 0 ? r : r * 0.45;
    const a = ((i * 36 - 90) * Math.PI) / 180;
    return `${(cx + radius * Math.cos(a)).toFixed(2)},${(cy + radius * Math.sin(a)).toFixed(2)}`;
  }).join(" ");
  return (
    <polygon
      points={points}
      strokeWidth={0.8}
      strokeLinejoin="round"
      className="fill-warning stroke-surface-raised"
    />
  );
}

/** Two pills on the People List box: its type tag and its source tag. */
const LIST_TAGS = [
  { x: 186, width: 34, label: "raw" },
  { x: 224, width: 46, label: "Thrive" },
] as const;

/**
 * The three ways into a People List, the new one starred; the list it makes
 * (raw, tagged Thrive, with its stage columns); and where it can go next, a
 * Thrive search, where its people start as Identified. Combine & Filter has
 * its own slide, so it is not drawn here.
 */
function ThriveImportDrawing() {
  const line = "fill-none stroke-accent";
  return (
    <svg
      viewBox="0 0 340 196"
      aria-hidden="true"
      focusable="false"
      className="h-auto w-full max-w-sm"
    >
      {IMPORT_SOURCES.map((s) => (
        <g key={s.title} data-testid={s.isNew ? "whats-new-thrive-source" : undefined}>
          <rect
            x={s.x}
            y={6}
            width={IMPORT_SOURCE_WIDTH}
            height={42}
            rx={6}
            strokeWidth={s.isNew ? 2 : 1}
            className={s.isNew ? "fill-accent-muted stroke-accent" : "fill-surface stroke-border"}
          />
          <Label x={s.x + IMPORT_SOURCE_WIDTH / 2} y={24} tone={s.isNew ? "bold" : "text"}>
            {s.title}
          </Label>
          <Label x={s.x + IMPORT_SOURCE_WIDTH / 2} y={38} tone="sub">
            {s.sub}
          </Label>
          {s.isNew ? <NewStar cx={s.x + IMPORT_SOURCE_WIDTH - 4} cy={8} /> : null}
        </g>
      ))}
      <path d="M56 48 V62 H284 V48 M170 48 V62 M170 62 V74" strokeWidth={1.6} className={line} />
      <path d="M164 70 L170 78 L176 70" strokeWidth={1.6} className={line} />
      <rect x={50} y={80} width={240} height={46} rx={7} className="fill-surface stroke-border" />
      <Row x={64} y={99} tone="bold">
        People List
      </Row>
      {LIST_TAGS.map((tag) => (
        <g key={tag.label} data-testid="whats-new-thrive-tag">
          <rect
            x={tag.x}
            y={88}
            width={tag.width}
            height={16}
            rx={8}
            className="fill-accent-muted stroke-accent"
          />
          <Label x={tag.x + tag.width / 2} y={100} tone="sub">
            {tag.label}
          </Label>
        </g>
      ))}
      <Label x={170} y={118} tone="sub">
        Stage · In stage · Added
      </Label>
      <path d="M170 126 V140" strokeWidth={1.6} className={line} />
      <path d="M164 136 L170 144 L176 136" strokeWidth={1.6} className={line} />
      <rect
        x={50}
        y={146}
        width={240}
        height={44}
        rx={7}
        className="fill-success-muted stroke-success"
      />
      <Label x={170} y={164} tone="bold">
        Add to a Thrive search
      </Label>
      <Label x={170} y={180} tone="sub">
        candidacies start as Identified
      </Label>
    </svg>
  );
}

/** One report radio of the panel drawing; `checked` is the selected one. */
function ReportRadio({
  x,
  label,
  count,
  checked = false,
}: {
  x: number;
  label: string;
  count?: { x: number; n: number };
  checked?: boolean;
}) {
  return (
    <g>
      {checked ? (
        <rect x={x - 6} y={75} width={64} height={17} rx={4} className="fill-accent-muted" />
      ) : null}
      <circle
        cx={x + 2}
        cy={83.5}
        r={3.5}
        className={checked ? "fill-accent stroke-accent" : "fill-none stroke-text-muted"}
      />
      <text
        x={x + 10}
        y={87}
        fontSize={9.5}
        className={checked ? "fill-text font-semibold" : "fill-text"}
      >
        {label}
      </text>
      {count ? (
        <text x={count.x} y={87} fontSize={9.5} className="fill-text-secondary">
          {count.n}
        </text>
      ) : null}
    </g>
  );
}

/**
 * The candidate panel's top, schematic (2026-10-27): the fictional Gregory
 * Tan, the four reports and [⬇ Download PDF | ▾] in ONE box, as the panel
 * draws them since Jared's "the white background should include the button
 * to join it", and the version line under them. An SVG on a fixed canvas like
 * the other slides, so it scales to the picture column (about 350px at a
 * 1280px window) instead of spilling out of it (/pr-review #1105 iter 1), and
 * token fills, so it follows the theme. The box sits 8 units inside the card
 * on both sides, so its border never doubles the card's (iter 2).
 */
function FourReportsDrawing() {
  return (
    <svg
      viewBox="0 0 428 140"
      aria-hidden="true"
      focusable="false"
      data-testid="whats-new-four-reports"
      className="h-auto w-full max-w-md"
    >
      <rect x={4} y={4} width={420} height={132} rx={8} className="fill-surface stroke-border" />
      <circle cx={28} cy={30} r={14} className="fill-accent-muted" />
      <text x={28} y={34} textAnchor="middle" fontSize={10} className="fill-accent-ink font-bold">
        GT
      </text>
      <text x={50} y={27} fontSize={12} className="fill-text font-bold">
        Gregory Tan
      </text>
      <text x={50} y={41} fontSize={9.5} className="fill-text-secondary">
        CFO at Stonebridge Retail Group
      </text>
      <rect x={362} y={20} width={50} height={18} rx={9} className="fill-success-muted" />
      <text
        x={387}
        y={32.5}
        textAnchor="middle"
        fontSize={9.5}
        className="fill-success-ink font-semibold"
      >
        Placed
      </text>
      <g data-testid="whats-new-four-reports-box">
        <rect
          data-testid="whats-new-four-reports-box-frame"
          x={12}
          y={54}
          width={404}
          height={46}
          rx={7}
          className="fill-surface stroke-border"
        />
        <text x={20} y={68} fontSize={8.5} className="fill-text-muted font-semibold">
          ASSESSMENTS
        </text>
        <ReportRadio x={24} label="Recruiter" checked />
        <ReportRadio x={92} label="Client" />
        <path d="M132 60 V94" className="stroke-border" />
        <text x={140} y={68} fontSize={8.5} className="fill-text-muted font-semibold">
          REFERENCES
        </text>
        <ReportRadio x={144} label="Official" count={{ x: 192, n: 2 }} />
        <ReportRadio x={206} label="Backchannel" count={{ x: 282, n: 1 }} />
        <path d="M294 60 V94" className="stroke-border" />
        <g data-testid="whats-new-four-reports-download">
          <rect
            data-testid="whats-new-four-reports-download-frame"
            x={300}
            y={65}
            width={108}
            height={24}
            rx={5}
            className="fill-accent"
          />
          <path
            d="M309 73 V80 M306 77.5 L309 80.5 L312 77.5 M305.5 83 H312.5"
            strokeWidth={1.2}
            className="fill-none stroke-bg"
          />
          <text x={316} y={80.5} fontSize={9} className="fill-bg font-semibold">
            Download PDF
          </text>
          <path d="M388 68 V86" className="stroke-bg" />
          <path d="M394 75 L397.5 78.5 L401 75" strokeWidth={1.2} className="fill-none stroke-bg" />
        </g>
      </g>
      <text x={12} y={122} fontSize={10} className="fill-text">
        <tspan className="font-semibold">Recruiter Assessment</tspan>
        <tspan className="fill-text-secondary"> · Version 2 · Matches Thrive</tspan>
      </text>
    </svg>
  );
}

/** One panel of the prompt viewer, on the raised surface it uses since #1032. */
function PromptPanel({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-sm border border-border bg-surface-raised px-2 py-1.5">
      <span className="text-nano font-semibold uppercase tracking-wide text-text-muted">
        {heading}
      </span>
      <span className="whitespace-pre-wrap break-words font-mono text-nano text-text">
        {children}
      </span>
    </div>
  );
}

function PromptViewerMiniature() {
  return (
    <div
      aria-hidden="true"
      className="flex w-full max-w-sm flex-col gap-2 rounded-md border border-border bg-surface p-3"
    >
      <div className="flex items-center gap-2">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent-muted text-micro font-semibold text-accent-ink">
          EM
        </span>
        <span className="flex min-w-0 flex-col">
          <span className="text-label font-semibold text-text">Elena Marchetti</span>
          <span className="text-micro text-text-muted">Operations leaders · v2</span>
        </span>
        <span className="ml-auto border-b-2 border-accent text-micro font-semibold text-accent-ink">
          AI Review
        </span>
      </div>
      <div className="flex items-center gap-2">
        <span className="rounded-pill bg-success-muted px-2 py-0.5 text-micro font-semibold text-success-ink">
          AI score 2.80
        </span>
        <span className="text-micro text-text-secondary">all criteria met</span>
      </div>
      <span className="text-micro font-semibold uppercase tracking-wide text-accent-ink">
        Prompt
      </span>
      <PromptPanel heading="Thrive notes">
        “Met in 2024; strong on multi-site operations, open to a COO role.”
      </PromptPanel>
      <PromptPanel heading="Thrive assessments">
        Recruiter · 4/5 — “Ready for a COO seat.”
      </PromptPanel>
      <PromptPanel heading="Rationale">
        Three lean plant rollouts; notes and assessment agree.
      </PromptPanel>
    </div>
  );
}

/** Left-aligned text for the ChopBook drawing's rows and record. */
function Row({
  x,
  y,
  children,
  tone = "text",
}: {
  x: number;
  y: number;
  children: ReactNode;
  tone?: "text" | "muted" | "bold" | "accent";
}) {
  const className = {
    text: "fill-text",
    muted: "fill-text-secondary",
    bold: "fill-text font-bold",
    accent: "fill-accent-ink font-semibold",
  }[tone];
  return (
    <text x={x} y={y} fontSize={11} className={className}>
      {children}
    </text>
  );
}

/** A PitchBook results card: four rows, three ticked. Fictional companies. */
const PITCHBOOK_ROWS = [
  { y: 32, name: "Halvorsen Industrial", ticked: true },
  { y: 50, name: "Brightwater Foods", ticked: true },
  { y: 68, name: "Calder Logistics", ticked: false },
  { y: 86, name: "Northgate Medical", ticked: true },
] as const;

/** Geometry from artifact v16, slide 5: a 340 × 250 canvas. */
function ChopBookDrawing() {
  const line = "fill-none stroke-accent";
  return (
    <svg
      viewBox="0 0 340 250"
      aria-hidden="true"
      focusable="false"
      className="h-auto w-full max-w-sm"
    >
      <rect x={6} y={6} width={160} height={104} rx={6} className="fill-surface stroke-border" />
      <Label x={86} y={22} tone="bold">
        PitchBook · results
      </Label>
      {PITCHBOOK_ROWS.map((row) => (
        <g key={row.name} data-testid={row.ticked ? "whats-new-chopbook-ticked" : undefined}>
          <rect
            x={16}
            y={row.y}
            width={10}
            height={10}
            rx={2}
            className={row.ticked ? "fill-accent stroke-accent" : "fill-none stroke-border"}
          />
          {row.ticked ? (
            <path
              d={`M18 ${row.y + 5} l2.5 2.5 l4 -5`}
              strokeWidth={1.6}
              className="fill-none stroke-surface"
            />
          ) : null}
          <Row x={34} y={row.y + 9} tone={row.ticked ? "text" : "muted"}>
            {row.name}
          </Row>
        </g>
      ))}
      <path d="M166 58 H186" strokeWidth={1.6} className={line} />
      <path d="M182 52 L190 58 L182 64" strokeWidth={1.6} className={line} />
      <rect
        x={192}
        y={30}
        width={142}
        height={56}
        rx={7}
        className="fill-accent-muted stroke-accent"
      />
      <Label x={263} y={52} tone="bold">
        ChopBook
      </Label>
      <Label x={263} y={70} tone="sub">
        captures the 3 ticked rows
      </Label>
      <path d="M263 86 V106" strokeWidth={1.6} className={line} />
      <path d="M257 102 L263 110 L269 102" strokeWidth={1.6} className={line} />
      <rect
        x={6}
        y={114}
        width={328}
        height={130}
        rx={7}
        className="fill-success-muted stroke-success"
      />
      <Row x={18} y={134} tone="bold">
        Search 4812
      </Row>
      <rect x={160} y={121} width={80} height={18} rx={9} className="fill-surface stroke-border" />
      <Label x={200} y={134} tone="sub">
        People Lists
      </Label>
      <rect x={246} y={121} width={80} height={18} rx={9} className="fill-accent stroke-accent" />
      <text
        x={286}
        y={134}
        textAnchor="middle"
        fontSize={9.5}
        className="fill-surface font-semibold"
      >
        Company Lists
      </text>
      <rect x={16} y={148} width={308} height={86} rx={6} className="fill-surface stroke-border" />
      <rect
        x={26}
        y={158}
        width={30}
        height={30}
        rx={6}
        className="fill-accent-muted stroke-accent"
      />
      <text x={41} y={177} textAnchor="middle" fontSize={12} className="fill-accent-ink font-bold">
        HI
      </text>
      <Row x={66} y={170} tone="bold">
        Halvorsen Industrial Group
      </Row>
      <Row x={66} y={185} tone="muted">
        Industrial machinery · Cleveland, OH
      </Row>
      <Row x={26} y={206}>
        Revenue $412M · 1,850 employees · Private
      </Row>
      <Row x={26} y={224} tone="accent">
        People we know here · 4
      </Row>
    </svg>
  );
}

/** A four-point sparkle, the AI mark (lucide's `Sparkles`, drawn flat). */
function Sparkle({ cx, cy, r = 6 }: { cx: number; cy: number; r?: number }) {
  const w = r * 0.3;
  return (
    <path
      d={`M${cx} ${cy - r} L${cx + w} ${cy - w} L${cx + r} ${cy} L${cx + w} ${cy + w} L${cx} ${cy + r} L${cx - w} ${cy + w} L${cx - r} ${cy} L${cx - w} ${cy - w} Z`}
      className="fill-info"
    />
  );
}

/** The recruiter's own criteria, then the two the AI suggests from Thrive. */
const OWN_CRITERIA = ["CFO experience in a PE-backed company", "Led an exit or IPO"] as const;
const SUGGESTED_CRITERIA = [
  { text: "Based in the US Midwest", evidence: "client · 6 rejections" },
  { text: "Industrial or manufacturing background", evidence: "team · 4 comments" },
] as const;

/**
 * The AI Review criteria box, two criteria the recruiter wrote, and under it
 * the violet AI section: a sparkle, "Suggested by AI from Thrive", and two
 * suggestions with their evidence and an Add each.
 */
function CriteriaSuggestDrawing() {
  return (
    <svg
      viewBox="0 0 340 196"
      aria-hidden="true"
      focusable="false"
      className="h-auto w-full max-w-sm"
    >
      <rect x={10} y={6} width={320} height={66} rx={7} className="fill-surface stroke-border" />
      <Row x={24} y={24} tone="bold">
        Criteria
      </Row>
      {OWN_CRITERIA.map((text, i) => (
        <Row key={text} x={24} y={44 + i * 18}>
          {`${i + 1}. ${text}`}
        </Row>
      ))}
      <rect
        x={10}
        y={80}
        width={320}
        height={110}
        rx={7}
        strokeWidth={1.5}
        className="fill-info-muted stroke-info-strong"
      />
      <Sparkle cx={26} cy={96} />
      <text x={38} y={100} fontSize={11} className="fill-info font-bold">
        Suggested by AI from Thrive
      </text>
      {SUGGESTED_CRITERIA.map((s, i) => (
        <g key={s.text} data-testid="whats-new-suggested-criterion">
          <rect
            x={20}
            y={110 + i * 38}
            width={300}
            height={32}
            rx={6}
            className="fill-surface stroke-info-strong"
          />
          <Sparkle cx={32} cy={126 + i * 38} r={4.5} />
          <Row x={44} y={123 + i * 38}>
            {s.text}
          </Row>
          <Row x={44} y={136 + i * 38} tone="muted">
            {s.evidence}
          </Row>
          <rect
            x={272}
            y={117 + i * 38}
            width={40}
            height={18}
            rx={9}
            className="fill-surface stroke-info-strong"
          />
          <text x={292} y={130 + i * 38} textAnchor="middle" fontSize={10} className="fill-info">
            + Add
          </text>
        </g>
      ))}
    </svg>
  );
}

const PREVIEWS: Record<WhatsNewIllustration, ComponentType> = {
  bullseye: BullseyeDrawing,
  "combine-filter": CombineFilterDrawing,
  "ai-review": PromptViewerMiniature,
  chopbook: ChopBookDrawing,
  "thrive-import": ThriveImportDrawing,
  "four-reports": FourReportsDrawing,
  "criteria-suggest": CriteriaSuggestDrawing,
};

export function WhatsNewDrawing({ illustration }: { illustration: WhatsNewIllustration }) {
  const Preview = PREVIEWS[illustration];
  return <Preview />;
}
